import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { flushTokens, useAppDispatch, useAppSelector, useTokens } from '@pkg/hooks';
import { LocalStorageKeys, TwoFactorAuthType } from '@pkg/types';
import { removeLocalStorageItems } from '@pkg/utils';

import { apiFactory, Auth, Users } from '@app/client';
import { type Token } from '@app/client/common/structs';
import { setError } from '@app/store/actions/error';
import { addNotification } from '@app/store/actions/notification';
import { initializeUser } from '@app/store/actions/user';
import { type ErrorState } from '@app/store/types/error';
import { useAuthSidebar, useUser } from '@app/store/zustand';
import { resetUserEmojis } from '@app/store/zustand/emojis/useEmojisStore';

export function useLogin() {
    const dispatch = useAppDispatch();
    const { t } = useTranslation(['waitlist']);
    const navigate = useNavigate();
    const { closeAuthSidebar } = useAuthSidebar();

    const partnerConversionId = useAppSelector((rootState) => rootState.partnerIntegrations.partnerConversionId);
    const { setTokens } = useTokens();
    const { setIsLoading: setIsUserLoading } = useUser();
    const { postAuthRedirect } = useAuthSidebar();

    const { orderItems } = useAppSelector(({ picker }) => picker);
    const userId = useAppSelector((rootState) => rootState.user?.userAccount?.user.id);

    async function mergeDraftUser() {
        const hasPendingOrder = orderItems?.length > 0;
        const draftUserId = userId ? userId : undefined;

        try {
            if (hasPendingOrder && draftUserId) {
                const consumeDraftUser = await apiFactory().call(Users.consumeDraftUser, {
                    value: {
                        userId: draftUserId,
                    },
                });

                if (consumeDraftUser.ok) {
                    removeLocalStorageItems([LocalStorageKeys.DraftUserId]);
                    return;
                } else {
                    throw new Error();
                }
            }
        } catch (error) {
            dispatch(
                addNotification({
                    message: t('waitlist_login_failed_consume'),
                    severity: 'error',
                }),
            );
        }
    }

    async function loginWithPassword({ email, password }: { email: string; password: string }): Promise<Token | void> {
        setIsUserLoading(true);

        try {
            const loginResult = await apiFactory().call(Auth.login, {
                value: {
                    email: email.trim(),
                    password: password.trim(),
                    partner_conversion_id: partnerConversionId,
                },
            });

            if (loginResult.ok) {
                const token = loginResult.value;
                return loginWithToken(token);
            } else {
                throw new Error('AUTH_ERROR_LOGIN_FAILED');
            }
        } catch (error) {
            setIsUserLoading(false);
            flushTokens();
            dispatch(setError({ error: error as ErrorState, notify: true }));
        }
    }

    function sanitizeStorage() {
        // Make sure to do session cleanup of emojis removal notification related data in order to prevent
        // display of unrelated/outdated notification
        resetUserEmojis();
        removeLocalStorageItems([LocalStorageKeys.EmojiIds]);
    }

    async function loginWithToken(token: Token) {
        setIsUserLoading(true);
        apiFactory().ws?.closeWithoutReconnect();

        sanitizeStorage();
        setTokens(token);
        await mergeDraftUser();

        const twoFAShouldPrompt = (token.requires_2fa && token.requires_2fa !== TwoFactorAuthType.Invalid) || false;
        if (!twoFAShouldPrompt) {
            dispatch(initializeUser());
            postAuthenticate();
        }

        return token;
    }

    function postAuthenticate() {
        if (postAuthRedirect) {
            navigate(postAuthRedirect);
            closeAuthSidebar();
        }
    }

    return {
        loginWithPassword,
        loginWithToken,
        postAuthenticate,
        sanitizeStorage,
    };
}
